<template>
  <div class="pengaturan-keamanan-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Pengaturan Keamanan</h1>
      <p class="text-gray-600">Kelola pengaturan keamanan dan autentikasi sistem</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Session & Login Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start mb-4">
          <div class="p-2 bg-blue-100 rounded-lg mr-3">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Session & Login</h2>
            <p class="text-sm text-gray-600 mt-1">Pengaturan timeout dan percobaan login</p>
          </div>
        </div>
        
        <el-form 
          :model="securitySettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Session Timeout">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.sessionTimeout" 
                :min="5" 
                :max="1440"
                :step="5"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">menit</span>
            </div>
            <div class="form-hint">
              Waktu maksimal user tidak aktif sebelum otomatis logout ({{ formatHours(securitySettings.sessionTimeout) }})
            </div>
          </el-form-item>

          <el-form-item label="Max Login Attempts">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.maxLoginAttempts" 
                :min="3" 
                :max="10"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">kali</span>
            </div>
            <div class="form-hint">
              Jumlah percobaan login gagal sebelum akun diblokir sementara
            </div>
          </el-form-item>

          <el-form-item label="Lockout Duration">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.lockoutDuration" 
                :min="5" 
                :max="120"
                :step="5"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">menit</span>
            </div>
            <div class="form-hint">
              Durasi akun diblokir setelah melebihi maksimal percobaan login
            </div>
          </el-form-item>

          <el-form-item label="Remember Me Duration">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.rememberMeDuration" 
                :min="1" 
                :max="30"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">hari</span>
            </div>
            <div class="form-hint">
              Durasi "Remember Me" untuk tetap login
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- Password Policy Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start mb-4">
          <div class="p-2 bg-green-100 rounded-lg mr-3">
            <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Password Policy</h2>
            <p class="text-sm text-gray-600 mt-1">Aturan dan persyaratan password</p>
          </div>
        </div>
        
        <el-form 
          :model="securitySettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Minimum Password Length">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.minPasswordLength" 
                :min="6" 
                :max="32"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-20">karakter</span>
            </div>
            <div class="form-hint">
              Panjang minimal password yang diizinkan
            </div>
          </el-form-item>

          <el-form-item label="Password Expiry">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.passwordExpiry" 
                :min="0" 
                :max="365"
                :step="30"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">hari</span>
            </div>
            <div class="form-hint">
              Masa berlaku password sebelum harus diganti (0 = tidak ada batas)
            </div>
          </el-form-item>

          <el-form-item label="Password History">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="securitySettings.passwordHistory" 
                :min="0" 
                :max="10"
                controls-position="right"
                :disabled="saving.security"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">kali</span>
            </div>
            <div class="form-hint">
              Jumlah password lama yang tidak boleh digunakan kembali
            </div>
          </el-form-item>

          <div class="border-t pt-4 mt-2">
            <h3 class="font-medium text-gray-700 mb-3">Persyaratan Kompleksitas Password</h3>
            
            <div class="space-y-3">
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label class="flex items-center flex-1 cursor-pointer" for="requireUppercase">
                  <span class="text-gray-700">Huruf Besar (A-Z)</span>
                </label>
                <el-switch
                  id="requireUppercase"
                  v-model="securitySettings.requireUppercase"
                  :disabled="saving.security"
                />
              </div>

              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label class="flex items-center flex-1 cursor-pointer" for="requireLowercase">
                  <span class="text-gray-700">Huruf Kecil (a-z)</span>
                </label>
                <el-switch
                  id="requireLowercase"
                  v-model="securitySettings.requireLowercase"
                  :disabled="saving.security"
                />
              </div>

              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label class="flex items-center flex-1 cursor-pointer" for="requireNumbers">
                  <span class="text-gray-700">Angka (0-9)</span>
                </label>
                <el-switch
                  id="requireNumbers"
                  v-model="securitySettings.requireNumbers"
                  :disabled="saving.security"
                />
              </div>

              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <label class="flex items-center flex-1 cursor-pointer" for="requireSpecialChars">
                  <span class="text-gray-700">Karakter Khusus (!@#$%^&*)</span>
                </label>
                <el-switch
                  id="requireSpecialChars"
                  v-model="securitySettings.requireSpecialChars"
                  :disabled="saving.security"
                />
              </div>
            </div>

            <div class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p class="text-sm text-blue-800">
                <span class="font-medium">Contoh password valid:</span> 
                <code class="ml-2 px-2 py-1 bg-white rounded">{{ generateExamplePassword() }}</code>
              </p>
            </div>
          </div>
        </el-form>
      </div>

      <!-- Two-Factor Authentication Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start mb-4">
          <div class="p-2 bg-purple-100 rounded-lg mr-3">
            <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Two-Factor Authentication (2FA)</h2>
            <p class="text-sm text-gray-600 mt-1">Keamanan tambahan dengan verifikasi dua langkah</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-purple-500 bg-purple-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Aktifkan 2FA</h3>
              <p class="text-sm text-gray-600 mt-1">
                Memerlukan kode verifikasi tambahan saat login untuk keamanan ekstra
              </p>
            </div>
            <el-switch
              v-model="securitySettings.twoFactorAuth"
              :disabled="saving.security"
            />
          </div>

          <div v-if="securitySettings.twoFactorAuth" class="pl-4 space-y-4">
            <el-form 
              :model="securitySettings" 
              label-width="220px" 
              label-position="left"
              class="settings-form"
            >
              <el-form-item label="Metode 2FA">
                <el-select 
                  v-model="securitySettings.twoFactorMethod" 
                  placeholder="Pilih metode"
                  :disabled="saving.security"
                  class="w-full"
                >
                  <el-option label="Email OTP" value="email" />
                  <el-option label="SMS OTP" value="sms" />
                  <el-option label="Authenticator App (Google/Microsoft)" value="app" />
                </el-select>
                <div class="form-hint">
                  Pilih metode pengiriman kode verifikasi
                </div>
              </el-form-item>

              <el-form-item label="Wajib untuk Semua User">
                <el-switch
                  v-model="securitySettings.require2FAForAll"
                  active-text="Ya"
                  inactive-text="Tidak"
                  :disabled="saving.security"
                />
                <div class="form-hint">
                  Jika aktif, semua user wajib menggunakan 2FA
                </div>
              </el-form-item>

              <el-form-item label="Grace Period">
                <div class="flex items-center gap-3">
                  <el-input-number 
                    v-model="securitySettings.twoFactorGracePeriod" 
                    :min="0" 
                    :max="30"
                    controls-position="right"
                    :disabled="saving.security"
                    class="flex-1"
                  />
                  <span class="text-sm text-gray-600 w-16">hari</span>
                </div>
                <div class="form-hint">
                  Waktu toleransi bagi user untuk mengaktifkan 2FA
                </div>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>

      <!-- Additional Security Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start mb-4">
          <div class="p-2 bg-orange-100 rounded-lg mr-3">
            <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Keamanan Tambahan</h2>
            <p class="text-sm text-gray-600 mt-1">Fitur keamanan dan proteksi lainnya</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-orange-500 bg-orange-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Require PIN Security</h3>
              <p class="text-sm text-gray-600 mt-1">
                Memerlukan PIN tambahan untuk mengakses menu dengan keamanan tinggi
              </p>
            </div>
            <el-switch
              v-model="securitySettings.requirePinSecurity"
              :disabled="saving.security"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-red-500 bg-red-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Force Logout on Password Change</h3>
              <p class="text-sm text-gray-600 mt-1">
                Logout otomatis dari semua device saat password diganti
              </p>
            </div>
            <el-switch
              v-model="securitySettings.forceLogoutOnPasswordChange"
              :disabled="saving.security"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-yellow-500 bg-yellow-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">IP Whitelist</h3>
              <p class="text-sm text-gray-600 mt-1">
                Batasi akses hanya dari IP address tertentu
              </p>
            </div>
            <el-switch
              v-model="securitySettings.enableIpWhitelist"
              :disabled="saving.security"
            />
          </div>

          <div v-if="securitySettings.enableIpWhitelist" class="pl-4">
            <el-form 
              :model="securitySettings" 
              label-width="220px" 
              label-position="left"
              class="settings-form"
            >
              <el-form-item label="Allowed IP Addresses">
                <el-input 
                  v-model="securitySettings.allowedIPs"
                  type="textarea"
                  :rows="4"
                  placeholder="Masukkan IP address yang diizinkan (pisahkan dengan koma atau baris baru)"
                  :disabled="saving.security"
                />
                <div class="form-hint">
                  Contoh: 192.168.1.1, 10.0.0.0/24
                </div>
              </el-form-item>
            </el-form>
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-indigo-500 bg-indigo-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Device Tracking</h3>
              <p class="text-sm text-gray-600 mt-1">
                Lacak dan kelola device yang digunakan untuk login
              </p>
            </div>
            <el-switch
              v-model="securitySettings.enableDeviceTracking"
              :disabled="saving.security"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-pink-500 bg-pink-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Login Notification</h3>
              <p class="text-sm text-gray-600 mt-1">
                Kirim notifikasi saat ada login dari device atau lokasi baru
              </p>
            </div>
            <el-switch
              v-model="securitySettings.loginNotification"
              :disabled="saving.security"
            />
          </div>
        </div>
      </div>

      <!-- Security Audit Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start mb-4">
          <div class="p-2 bg-teal-100 rounded-lg mr-3">
            <svg class="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Security Audit & Logging</h2>
            <p class="text-sm text-gray-600 mt-1">Pencatatan aktivitas keamanan</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-teal-500 bg-teal-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Enable Activity Logging</h3>
              <p class="text-sm text-gray-600 mt-1">
                Catat semua aktivitas user untuk audit keamanan
              </p>
            </div>
            <el-switch
              v-model="securitySettings.enableActivityLogging"
              :disabled="saving.security"
            />
          </div>

          <div v-if="securitySettings.enableActivityLogging" class="pl-4">
            <el-form 
              :model="securitySettings" 
              label-width="220px" 
              label-position="left"
              class="settings-form"
            >
              <el-form-item label="Log Retention Period">
                <div class="flex items-center gap-3">
                  <el-input-number 
                    v-model="securitySettings.logRetentionDays" 
                    :min="7" 
                    :max="365"
                    :step="7"
                    controls-position="right"
                    :disabled="saving.security"
                    class="flex-1"
                  />
                  <span class="text-sm text-gray-600 w-16">hari</span>
                </div>
                <div class="form-hint">
                  Durasi penyimpanan log aktivitas
                </div>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>

      <!-- Security Score Card -->
      <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow-md p-6 border-2 border-blue-200">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Security Score</h3>
        <div class="flex items-center justify-between mb-4">
          <div class="flex-1">
            <div class="flex items-center gap-3">
              <div class="relative w-20 h-20">
                <svg class="transform -rotate-90 w-20 h-20">
                  <circle cx="40" cy="40" r="36" stroke="#e5e7eb" stroke-width="8" fill="none"/>
                  <circle 
                    cx="40" 
                    cy="40" 
                    r="36" 
                    :stroke="getScoreColor()" 
                    stroke-width="8" 
                    fill="none"
                    :stroke-dasharray="226"
                    :stroke-dashoffset="226 - (226 * securityScore / 100)"
                    class="transition-all duration-500"
                  />
                </svg>
                <div class="absolute inset-0 flex items-center justify-center">
                  <span class="text-xl font-bold" :style="{ color: getScoreColor() }">{{ securityScore }}</span>
                </div>
              </div>
              <div>
                <p class="text-2xl font-bold" :style="{ color: getScoreColor() }">{{ getScoreLabel() }}</p>
                <p class="text-sm text-gray-600 mt-1">{{ getScoreDescription() }}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div class="bg-white p-3 rounded-lg">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Password Policy</span>
              <span class="text-sm font-semibold" :class="getPasswordPolicyScore() >= 70 ? 'text-green-600' : 'text-orange-600'">
                {{ getPasswordPolicyScore() }}%
              </span>
            </div>
          </div>
          <div class="bg-white p-3 rounded-lg">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">2FA Status</span>
              <span class="text-sm font-semibold" :class="securitySettings.twoFactorAuth ? 'text-green-600' : 'text-red-600'">
                {{ securitySettings.twoFactorAuth ? 'Aktif' : 'Nonaktif' }}
              </span>
            </div>
          </div>
          <div class="bg-white p-3 rounded-lg">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Session Security</span>
              <span class="text-sm font-semibold text-green-600">
                {{ securitySettings.sessionTimeout }} min
              </span>
            </div>
          </div>
          <div class="bg-white p-3 rounded-lg">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Login Protection</span>
              <span class="text-sm font-semibold text-green-600">
                {{ securitySettings.maxLoginAttempts }}x attempts
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-700 font-medium">Simpan Perubahan</p>
            <p class="text-sm text-gray-600">Pastikan pengaturan keamanan sudah sesuai</p>
          </div>
          
          <div class="flex gap-3">
            <el-button @click="resetSettings" :disabled="saving.security">
              Reset
            </el-button>
            <el-button 
              type="primary" 
              @click="saveSecuritySettings" 
              :loading="saving.security"
            >
              Simpan Perubahan
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Last Updated Info -->
    <div v-if="lastUpdated" class="mt-4 text-center text-sm text-gray-500">
      Terakhir diupdate: {{ formatDate(lastUpdated) }}
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Axios from '@/utils/axiosConfig';

export default {
  name: "PengaturanKeamananView",
  data() {
    return {
      loading: false,
      saving: {
        security: false
      },
      // Security Settings Data
      securitySettings: {
        // Session & Login
        sessionTimeout: 30,
        maxLoginAttempts: 5,
        lockoutDuration: 15,
        rememberMeDuration: 7,
        
        // Password Policy
        minPasswordLength: 8,
        passwordExpiry: 90,
        passwordHistory: 5,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSpecialChars: true,
        
        // Two-Factor Auth
        twoFactorAuth: false,
        twoFactorMethod: 'email',
        require2FAForAll: false,
        twoFactorGracePeriod: 7,
        
        // Additional Security
        requirePinSecurity: false,
        forceLogoutOnPasswordChange: true,
        enableIpWhitelist: false,
        allowedIPs: '',
        enableDeviceTracking: true,
        loginNotification: true,
        
        // Audit & Logging
        enableActivityLogging: true,
        logRetentionDays: 90
      },
      originalSettings: {},
      lastUpdated: null
    };
  },
  computed: {
    ...mapState(['host']),
    
    securityScore() {
      let score = 0;
      const settings = this.securitySettings;
      
      // Session security (15 points)
      if (settings.sessionTimeout <= 60) score += 10;
      if (settings.maxLoginAttempts <= 5) score += 5;
      
      // Password policy (35 points)
      if (settings.minPasswordLength >= 8) score += 5;
      if (settings.passwordExpiry > 0 && settings.passwordExpiry <= 90) score += 5;
      if (settings.passwordHistory >= 3) score += 5;
      if (settings.requireUppercase) score += 5;
      if (settings.requireLowercase) score += 5;
      if (settings.requireNumbers) score += 5;
      if (settings.requireSpecialChars) score += 5;
      
      // 2FA (25 points)
      if (settings.twoFactorAuth) {
        score += 15;
        if (settings.require2FAForAll) score += 10;
      }
      
      // Additional security (25 points)
      if (settings.requirePinSecurity) score += 5;
      if (settings.forceLogoutOnPasswordChange) score += 5;
      if (settings.enableDeviceTracking) score += 5;
      if (settings.loginNotification) score += 5;
      if (settings.enableActivityLogging) score += 5;
      
      return score;
    }
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await Axios.get(`${this.host}/api/settings/security`);
        
        if (response.data) {
          const data = response.data.data || response.data;
          this.securitySettings = {
            ...this.securitySettings,
            ...data
          };
          // Save original settings for reset
          this.originalSettings = JSON.parse(JSON.stringify(this.securitySettings));
          this.lastUpdated = data.updatedAt || null;
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        this.$message.warning('Gagal memuat pengaturan. Menggunakan nilai default.');
      } finally {
        this.loading = false;
      }
    },

    async saveSecuritySettings() {
      // Validation
      if (this.securitySettings.minPasswordLength < 6) {
        this.$message.error('Panjang minimal password harus minimal 6 karakter');
        return;
      }

      if (this.securitySettings.sessionTimeout < 5) {
        this.$message.error('Session timeout minimal 5 menit');
        return;
      }

      if (this.securitySettings.enableIpWhitelist && !this.securitySettings.allowedIPs) {
        this.$message.error('IP whitelist harus diisi jika fitur diaktifkan');
        return;
      }

      if (this.securitySettings.twoFactorAuth && this.securitySettings.require2FAForAll) {
        this.$confirm(
          'Mengaktifkan 2FA wajib untuk semua user akan memaksa semua user untuk mengatur 2FA pada login berikutnya. Lanjutkan?',
          'Konfirmasi',
          {
            confirmButtonText: 'Ya, Lanjutkan',
            cancelButtonText: 'Batal',
            type: 'warning'
          }
        ).then(() => {
          this.performSave();
        }).catch(() => {
          // User cancelled
        });
        return;
      }

      this.performSave();
    },

    async performSave() {
      this.saving.security = true;
      try {
        const response = await Axios.post(`${this.host}/api/settings/security`, this.securitySettings);
        
        if (response.data) {
          this.lastUpdated = new Date();
          this.originalSettings = JSON.parse(JSON.stringify(this.securitySettings));
          this.$message.success('Pengaturan keamanan berhasil disimpan!');
        }
      } catch (error) {
        console.error('Error saving security settings:', error);
        this.$message.error('Gagal menyimpan pengaturan keamanan');
      } finally {
        this.saving.security = false;
      }
    },

    resetSettings() {
      this.$confirm('Apakah Anda yakin ingin mereset semua perubahan?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Reset',
        cancelButtonText: 'Batal',
        type: 'warning'
      }).then(() => {
        this.securitySettings = JSON.parse(JSON.stringify(this.originalSettings));
        this.$message.info('Pengaturan direset ke nilai sebelumnya');
      }).catch(() => {
        // User cancelled
      });
    },

    formatHours(minutes) {
      if (minutes < 60) return `${minutes} menit`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) return `${hours} jam`;
      return `${hours} jam ${mins} menit`;
    },

    generateExamplePassword() {
      const examples = [];
      if (this.securitySettings.requireUppercase) examples.push('ABC');
      if (this.securitySettings.requireLowercase) examples.push('xyz');
      if (this.securitySettings.requireNumbers) examples.push('123');
      if (this.securitySettings.requireSpecialChars) examples.push('@#$');
      
      const example = examples.join('');
      const length = Math.max(this.securitySettings.minPasswordLength, 8);
      
      return example.padEnd(length, 'x');
    },

    getPasswordPolicyScore() {
      let score = 0;
      const s = this.securitySettings;
      
      if (s.minPasswordLength >= 8) score += 20;
      if (s.passwordExpiry > 0 && s.passwordExpiry <= 90) score += 15;
      if (s.passwordHistory >= 3) score += 15;
      if (s.requireUppercase) score += 12.5;
      if (s.requireLowercase) score += 12.5;
      if (s.requireNumbers) score += 12.5;
      if (s.requireSpecialChars) score += 12.5;
      
      return Math.round(score);
    },

    getScoreColor() {
      if (this.securityScore >= 80) return '#10b981'; // green
      if (this.securityScore >= 60) return '#f59e0b'; // orange
      return '#ef4444'; // red
    },

    getScoreLabel() {
      if (this.securityScore >= 80) return 'Sangat Aman';
      if (this.securityScore >= 60) return 'Cukup Aman';
      if (this.securityScore >= 40) return 'Kurang Aman';
      return 'Tidak Aman';
    },

    getScoreDescription() {
      if (this.securityScore >= 80) return 'Keamanan sistem Anda sangat baik';
      if (this.securityScore >= 60) return 'Keamanan sistem Anda cukup baik, pertimbangkan peningkatan';
      if (this.securityScore >= 40) return 'Keamanan sistem Anda perlu ditingkatkan';
      return 'Keamanan sistem Anda sangat lemah, segera perbaiki';
    },

    formatDate(date) {
      if (!date) return '';
      
      const d = new Date(date);
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return d.toLocaleDateString('id-ID', options);
    }
  }
};
</script>

<style scoped>
.pengaturan-keamanan-container {
  max-width: 1200px;
  margin: 0 auto;
}

.settings-form {
  margin-top: 0;
}

.form-hint {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

code {
  font-family: 'Courier New', monospace;
  font-size: 13px;
}

/* Customize Element UI form items */
:deep(.el-form-item) {
  margin-bottom: 24px;
}

:deep(.el-form-item__label) {
  color: #374151;
  font-weight: 500;
}

:deep(.el-input__inner),
:deep(.el-textarea__inner) {
  border-radius: 8px;
}

:deep(.el-input-number) {
  width: 100%;
}

:deep(.el-switch) {
  height: auto;
}

:deep(.el-switch__label) {
  font-size: 14px;
}

:deep(.el-select) {
  width: 100%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pengaturan-keamanan-container {
    padding: 1rem;
  }

  :deep(.el-form-item) {
    flex-direction: column;
  }

  :deep(.el-form-item__label) {
    text-align: left !important;
    width: 100% !important;
    margin-bottom: 8px;
  }

  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}
</style>